const API_URL = 'https://emiratesscholar.com/system/api';

// Helper to get authorization headers
const getAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem('auth_token');
  return {
    'Accept': 'application/json',
    'Authorization': `Bearer ${token}`,
    ...extra
  };
};

// Helper to format date
const formatDateStr = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const day = date.getDate();
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const year = date.getFullYear();
  return `${day} ${month}, ${year}`;
};

// Deep recursive finder to extract the tickets array from any response layout
const findTicketsArray = (obj) => {
  if (!obj) return [];
  if (Array.isArray(obj)) return obj;
  if (typeof obj !== 'object') return [];
  
  // Check common key names
  if (Array.isArray(obj.tickets)) return obj.tickets;
  if (Array.isArray(obj.data)) return obj.data;
  if (Array.isArray(obj.ticket_list)) return obj.ticket_list;
  if (Array.isArray(obj.tickets_list)) return obj.tickets_list;
  if (Array.isArray(obj.list)) return obj.list;
  
  // Deep search
  for (const key of Object.keys(obj)) {
    if (Array.isArray(obj[key])) {
      return obj[key];
    }
    if (obj[key] && typeof obj[key] === 'object') {
      const nested = findTicketsArray(obj[key]);
      if (nested && nested.length > 0) return nested;
    }
  }
  return [];
};

// Robust recursive finder to locate the ticket object from the API response layout
const findTicketObject = (obj) => {
  if (!obj) return null;
  if (Array.isArray(obj)) {
    if (obj.length > 0) return findTicketObject(obj[0]);
    return null;
  }
  if (typeof obj !== 'object') return null;
  
  if ((obj.id || obj.ticket_id || obj.supportId) && (obj.subject || obj.title || obj.status !== undefined || obj.messages || obj.replies)) {
    return obj;
  }
  
  if (obj.ticket && typeof obj.ticket === 'object') {
    const res = findTicketObject(obj.ticket);
    if (res) return res;
  }
  if (obj.data && typeof obj.data === 'object') {
    const res = findTicketObject(obj.data);
    if (res) return res;
  }
  
  for (const key of Object.keys(obj)) {
    if (key !== 'ticket' && key !== 'data' && obj[key] && typeof obj[key] === 'object') {
      const nested = findTicketObject(obj[key]);
      if (nested) return nested;
    }
  }
  return null;
};

// Helper to map ticket data robustly matching Laravel database column variants
const mapTicket = (item, fallbackId = '') => {
  if (!item) return {};
  
  const idVal = item.id || item.ticket_id || item.supportId || item.ref_no || item.ticket_no || fallbackId || '';
  const statusVal = String(item.status || '').toLowerCase();
  
  // Support Laravel default ticket statuses (anything that isn't explicitly closed/0 is active, excluding success envelopes)
  const isClosed = statusVal === 'closed' || 
                   statusVal === '0' || 
                   item.status === 0 ||
                   item.status === '0';
  const isActive = !isClosed && statusVal !== 'success';
                   
  const status = isActive ? 'active' : 'closed';

  return {
    id: idVal ? (String(idVal).startsWith('#') ? String(idVal) : `#-${idVal}`) : `#-${fallbackId}`,
    supportId: String(idVal || fallbackId || ''),
    subject: item.subject || item.title || 'No Subject',
    category: item.category || item.type || 'Support Ticket',
    status: status,
    date: formatDateStr(item.created_at || item.updated_at || item.date || item.created_date),
    details: item.details || item.message || item.description || ''
  };
};

export const fetchTickets = async () => {
  try {
    const response = await fetch(`${API_URL}/ticket-list`, {
      method: 'GET',
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`Server responded with status ${response.status}`);
    }

    const data = await response.json();
    console.log('📡 [supportApi] ticket-list raw response:', data);

    // Deep-search for the tickets array
    const list = findTicketsArray(data);
    const mappedList = list.map(item => mapTicket(item));

    // Calculate stats dynamically on the frontend to avoid backend structure dependency
    const total = mappedList.length;
    const active = mappedList.filter(t => t.status === 'active').length;
    const closed = mappedList.filter(t => t.status === 'closed').length;

    return {
      tickets: mappedList,
      stats: { total, active, closed }
    };
  } catch (error) {
    console.error('Error fetching tickets:', error);
    return { tickets: [], stats: { total: 0, active: 0, closed: 0 } };
  }
};

// 2. GET Ticket Detail by ID
export const fetchTicketById = async (id) => {
  const cleanId = String(id).replace(/[#-]/g, '').trim();
  try {
    const response = await fetch(`${API_URL}/ticket-detail/${cleanId}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`Server responded with status ${response.status}`);
    }

    const data = await response.json();
    console.log('📡 [supportApi] ticket-detail raw response:', data);
    
    // Use robust finder for the ticket object (which resolves to data.Support)
    let ticketData = findTicketObject(data) || data;
    if (Array.isArray(ticketData) && ticketData.length > 0) {
      ticketData = ticketData[0];
    }

    // Support message/reply arrays located at root level (data.ticketDetails) or nested inside ticket object
    const rawMessages = data.ticketDetails ||
                        ticketData.messages || 
                        ticketData.replies || 
                        data.messages || 
                        data.replies || 
                        data.comments ||
                        ticketData.comments ||
                        [];
                        
    const mappedMessages = rawMessages.map((msg, index) => {
      // Determine if sender is the user who created the ticket (defaults to user if match, else support/admin)
      const ticketUserId = ticketData.user_id || (data.Support && data.Support.user_id);
      const isUser = (
        msg.sender === 'user' || 
        msg.sender_type === 'user' || 
        (msg.user_id && ticketUserId && String(msg.user_id) === String(ticketUserId))
      );
      const sender = isUser ? 'user' : 'support';
      
      return {
        id: msg.id || index,
        sender: sender,
        name: msg.sender_name || msg.user?.name || (sender === 'user' ? 'You' : 'Support Office'),
        initials: msg.initials || (sender === 'user' ? 'Y' : 'SO'),
        color: sender === 'user' ? 'blue' : 'amber',
        date: msg.created_at || msg.date || msg.time || '',
        message: msg.message || msg.reply || msg.details || msg.body || msg.comment || '',
        file: msg.file || null
      };
    });

    // Sort messages in chronological (ascending) order
    const sortedMessages = [...mappedMessages].sort((a, b) => {
      const dateA = a.date ? new Date(a.date).getTime() : 0;
      const dateB = b.date ? new Date(b.date).getTime() : 0;
      return dateA - dateB;
    });

    const mapped = mapTicket(ticketData, cleanId);
    return {
      ...mapped,
      messages: sortedMessages,
      details: ticketData.details || ticketData.message || ticketData.description || mapped.details || '',
      file: ticketData.file || null,
      fileLink: ticketData.fileLink || null,
      fileUrl: data.fileUrl || null
    };
  } catch (error) {
    console.error('Error fetching ticket by ID:', error);
    throw error;
  }
};

// 3. POST Reply to Ticket
export const sendReply = async (ticketId, message) => {
  const cleanId = String(ticketId).replace(/[#-]/g, '').trim();
  try {
    const response = await fetch(`${API_URL}/ticket-reply`, {
      method: 'POST',
      headers: getAuthHeaders({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify({
        ticket_id: cleanId,
        id: cleanId,
        details: message,
        message: message,
        reply: message
      })
    });

    if (!response.ok) {
      throw new Error(`Server responded with status ${response.status}`);
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    console.error('Error sending reply:', error);
    throw error;
  }
};

// 4. POST Create a New Support Ticket
export const createNewTicket = async (subject, details, file = null) => {
  try {
    const formData = new FormData();
    formData.append('subject', subject);
    formData.append('details', details);
    if (file) {
      formData.append('file', file);
    }

    const response = await fetch(`${API_URL}/new-support-ticket`, {
      method: 'POST',
      headers: getAuthHeaders(), // Boundary is set automatically by browser
      body: formData
    });

    if (!response.ok) {
      throw new Error(`Server responded with status ${response.status}`);
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    console.error('Error creating ticket:', error);
    throw error;
  }
};

// 5. Close Ticket
export const closeTicket = async (ticketId) => {
  const cleanId = String(ticketId).replace(/[#-]/g, '').trim();
  try {
    const response = await fetch(`${API_URL}/ticket-close`, {
      method: 'POST',
      headers: getAuthHeaders({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify({
        ticket_id: cleanId,
        id: cleanId
      })
    });
    
    if (!response.ok) {
      console.warn('Close ticket endpoint returned non-200 status');
    }
    return { success: true };
  } catch (error) {
    console.warn('Error closing ticket:', error);
    return { success: true };
  }
};